import { useEffect, useState } from 'react'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { systemValueOptionText } from '../../../generic/systemValues/systemValueLabel'
import { apiGet } from '../../../generic/Api_Functions'
import SmartTextField from '../../../generic/smarttextfield/SmartTextField'
import { Box, Grid } from '@mui/material'
import SmartSelect from '../../../generic/smartSelect/SmartSelect'

// Historia 44 — nº de líneas de referencia del campo Question (D4). La altura real la
// fija el bloque de 2 filas que ocupa (ver más abajo); esto solo evita que el textarea
// arranque con un tamaño mínimo desproporcionado. El texto que exceda ese alto se accede
// por scroll interno del propio campo (S2/A3).
const QUESTION_VISIBLE_ROWS = 4

/**
 *
 * @param {object} props
 * @param {import("../../../generic/utilities/objectStructures/workflow/workflow").Feature} props.editingFeature EditingFeature object
 * @param {Dispatch<SetStateAction<import("../../../generic/utilities/objectStructures/workflow/workflow").Feature>>} props.setEditingFeature editingFeature object setter
 * @param {string[]} props.usedFeatureCodes String array with the already used feature codes
 * @param {string[]} props.usedFeatureOrderValues String array with the already used feature order values
 * @param {boolean} props.isCreating Boolean value used to check if the feature is on creation mode or not.
 * @param {boolean} props.forceCheck  Boolean value used to force fields checks
 * @param {function(string string)} props.checkIfIsNotEmpty Method used to check if a given value is empty
 * @param {function(string)} props.checkFeatureCode Method used to check feature code
 * @param {function(string)} props.checkFeatureOrder Method used to check feature order
 * @param {function(string string)} props.checkIfIsNumber Method used to check if a given value is a number
 * @param {function(number number)} props.checkUpperAndLowerValues Method used to check lower and upper tolerance values
 * @param {JSX} props.AlertElement AlertElement
 * @returns
 */
const FeatureForm = ({ editingFeature, setEditingFeature, isCreating, forceCheck, checkIfIsNotEmpty, checkFeatureCode, checkFeatureOrder, checkIfIsNumber, checkUpperAndLowerValues, AlertElement, checkErrors, disabled }) => {
  /** @type {[import('../../../generic/utilities/objectStructures/globalStructures').IdName[], React.Dispatch<SetStateAction<import('../../../generic/utilities/objectStructures/globalStructures').IdName[]>>]} */
  const [inspectionPoints, setInspectionPoints] = useState()
  /** @type {[import('../../../generic/utilities/objectStructures/workflow/workflow').FeatureType[], React.Dispatch<SetStateAction<('../../../generic/utilities/objectStructures/workflow/workflow').FeatureType[]>>]} */
  const [featureTypes, setFeatureTypes] = useState()
  /** @type {[import('../../../generic/utilities/objectStructures/globalStructures').IdCode[], React.Dispatch<SetStateAction<import('../../../generic/utilities/objectStructures/globalStructures').IdCode[]>>]} */
  const [units, setUnits] = useState()
  /** @type {[import('../../../generic/utilities/objectStructures/toolreference/toolReference').ToolReference[], React.Dispatch<SetStateAction<import('../../../generic/utilities/objectStructures/toolreference/toolReference').ToolReference[]>>]} */
  const [tools, setTools] = useState()
  const [firstLoad, setFirstLoad] = useState(true)
  const [forceToleranceCheck, setForceToleranceCheck] = useState(false)
  const { t_ } = useSmartTranslation()
  const qualitative = 'Qualitative'

  useEffect(() => {
    // Each catalogue loads on its own: a refused one (e.g. 403 on tool references for a profile
    // without TOOLS) only leaves its own list empty instead of emptying the whole form.
    const loadList = (resource, setList) =>
      apiGet(resource).then(setList).catch(() => setList([]))

    if (firstLoad) {
      Promise.all([
        loadList('inspectionPoints/all', setInspectionPoints),
        loadList('featureTypes', setFeatureTypes),
        loadList('units', setUnits),
        loadList('toolReference/byTechnology/INSPECT', setTools)
      ]).then(() => setFirstLoad(false))
    }
  }, [firstLoad])

  /**
   *
   * @param {import('../../../generic/utilities/objectStructures/toolreference/toolReference').ToolReference[]} toolReferences
   */
  const getSerializable = (toolReferences) => {
    if (!toolReferences || toolReferences.length === 0) {
      return ''
    }
    const param = toolReferences[0].parameterValues?.find(parameterValue => parameterValue.code === 'SERIALIZABLE')
    if (!param || !param.value || ((typeof param.value === 'string') && param.value.toLowerCase() === 'false')) {
      return t_('NO')
    }
    return t_('YES')
  }

  useEffect(() => {
    if (forceToleranceCheck) {
      setForceToleranceCheck(false)
    }
  }, [forceToleranceCheck])

  return (
    <>
      {AlertElement}
      <Grid container spacing={2} marginTop='0.5rem' marginBottom='0.5rem'>
        <Grid item xs={12} sm={1}>
          <SmartTextField
            label={t_('Order')}
            value={editingFeature.featureOrder}
            onChange={(newValue) => {
              const data = { ...editingFeature }
              data.featureOrder = newValue
              setEditingFeature(data)
            }}
            checkError={(value) => checkFeatureOrder(value) || checkIfIsNumber(value, t_('Order'))}
            forceCheckError={forceCheck}
            required
            style={{ width: '100%' }}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <SmartTextField
            label={t_('Code')}
            value={editingFeature.code}
            onChange={(newValue) => {
              const data = { ...editingFeature }
              data.code = newValue
              setEditingFeature(data)
            }}
            disabled={disabled}
            checkError={(value) => checkFeatureCode(value)}
            forceCheckError={forceCheck}
            required
            style={{ width: '100%' }}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <SmartTextField
            label={t_('Name')}
            value={editingFeature.name}
            onChange={(newValue) => {
              const data = { ...editingFeature }
              data.name = newValue
              setEditingFeature(data)
            }}
            checkError={(value) => checkIfIsNotEmpty(value, t_('Name'))}
            forceCheckError={forceCheck}
            required
            style={{ width: '100%' }}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SmartTextField
            label={t_('Description')}
            value={editingFeature.description}
            onChange={(newValue) => {
              const data = { ...editingFeature }
              data.description = newValue
              setEditingFeature(data)
            }}
            style={{ width: '100%' }}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <SmartSelect
            label={t_('Feature Type') + ' *'}
            value={editingFeature.featureType?.name}
            selectableOptions={featureTypes?.map((type) => type.name)}
            optionLabelTransform={(name) => systemValueOptionText(t_, 'feature_type', featureTypes, name)}
            onChange={(newValue) => {
              const data = { ...editingFeature }
              const featureType = featureTypes?.find(ft => ft.name === newValue)
              data.featureType = featureType
              if (featureType.dataType !== 'RANGE') {
                setForceToleranceCheck(true)
                data.value = ''
                data.upperTolerance = ''
                data.lowerTolerance = ''
              }
              setEditingFeature(data)
            }}
            error={(value) => checkErrors && checkIfIsNotEmpty(value, t_('Type'))}
            forceCheckError={forceCheck}
            style={{ width: '100%' }}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={1}>
          <SmartTextField
            label={t_('Frequency')}
            value={editingFeature.frequency}
            onChange={(newValue) => {
              const data = { ...editingFeature }
              data.frequency = newValue
              setEditingFeature(data)
            }}
            disabled={!editingFeature.featureType || disabled}
            checkError={(value) => checkIfIsNumber(value, t_('Frequency'))}
            style={{ width: '100%' }}
          />
        </Grid>

        {/* Historia 44 — filas 2-4 + Question como un único CSS Grid nativo (12 columnas x 3
            filas). Question ocupa las columnas 8-12 (mismo ancho que los spacers sm={5}
            originales). Se descarta el enfoque de celda normal del grid (Question como item
            más, ocupando gridRow) porque, aunque `minHeight:0`+`overflow:hidden` evitan que
            fuerce el crecimiento por su tamaño MÍNIMO, el algoritmo de sizing de CSS Grid sigue
            usando su tamaño preferido (max-content) en la fase de reparto de las filas
            abarcadas, desbordando unos px. Question se posiciona `position:absolute` anclado a
            las líneas del grid (gridColumn/gridRow siguen funcionando con position:absolute):
            así queda fuera del flujo — no participa en el sizing de las filas en ninguna fase,
            ni aporta tamaño mínimo ni preferido — y su contenedor de posicionamiento es
            exactamente el área que delimitan esas líneas, calculada a partir del resto de
            campos. También evita el solapamiento de la etiqueta con el borde superior (visible
            sin necesidad de overflow/padding de reserva), porque al no clipar nada puede
            sobresalir igual que en cualquier TextField normal. */}
        <Grid item xs={12}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2, position: 'relative' }}>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: '1 / 3' }, gridRow: { xs: 'auto', sm: '1 / 2' } }}>
              <SmartTextField
                label={t_('Nominal')}
                value={editingFeature.value}
                onChange={(newValue) => {
                  const data = { ...editingFeature }
                  data.value = newValue
                  setEditingFeature(data)
                }}
                disabled={disabled || !editingFeature.featureType || editingFeature.featureType?.dataType !== 'RANGE'}
                checkError={(value) => checkIfIsNumber(value, t_('Nominal')) || ((editingFeature.featureType?.dataType === 'RANGE') && checkIfIsNotEmpty(value, t_('Nominal')))}
                forceCheckError={forceCheck}
                required={editingFeature.featureType?.dataType === 'RANGE'}
                style={{ width: '100%' }}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: '3 / 5' }, gridRow: { xs: 'auto', sm: '1 / 2' } }}>
              <SmartTextField
                label={t_('Lower tolerance')}
                value={editingFeature.lowerTolerance}
                onChange={(newValue) => {
                  setForceToleranceCheck(true)
                  const data = { ...editingFeature }
                  data.lowerTolerance = newValue
                  setEditingFeature(data)
                }}
                disabled={disabled || !editingFeature.featureType || editingFeature.featureType?.dataType !== 'RANGE'}
                checkError={(value) => checkUpperAndLowerValues(editingFeature.upperTolerance, value) || (editingFeature.featureType?.dataType === 'RANGE' && editingFeature.upperTolerance && checkIfIsNotEmpty(value, t_('Lower tolerance')))}
                forceCheckError={forceCheck || forceToleranceCheck}
                required={editingFeature.featureType?.dataType === 'RANGE' && (editingFeature.upperTolerance || editingFeature.lowerTolerance)}
                style={{ width: '100%' }}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: '5 / 7' }, gridRow: { xs: 'auto', sm: '1 / 2' } }}>
              <SmartTextField
                label={t_('Upper tolerance')}
                value={editingFeature.upperTolerance}
                onChange={(newValue) => {
                  setForceToleranceCheck(true)
                  const data = { ...editingFeature }
                  data.upperTolerance = newValue
                  setEditingFeature(data)
                }}
                disabled={disabled || !editingFeature.featureType || editingFeature.featureType?.dataType !== 'RANGE'}
                checkError={(value) => checkUpperAndLowerValues(value, editingFeature.lowerTolerance) || (editingFeature.featureType?.dataType === 'RANGE' && editingFeature.lowerTolerance && checkIfIsNotEmpty(value, t_('Upper tolerance')))}
                forceCheckError={forceCheck || forceToleranceCheck}
                required={editingFeature.featureType?.dataType === 'RANGE' && (editingFeature.upperTolerance || editingFeature.lowerTolerance)}
                style={{ width: '100%' }}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: '7 / 8' }, gridRow: { xs: 'auto', sm: '1 / 2' } }}>
              <SmartSelect
                label={t_('Unit') + ((editingFeature.featureType?.dataType === 'RANGE') ? ' *' : '')}
                value={editingFeature.unit?.code}
                selectableOptions={units?.map((type) => type.code)}
                onChange={(newValue) => {
                  const data = { ...editingFeature }
                  const unit = units?.find(un => un.code === newValue)
                  data.unit = unit
                  setEditingFeature(data)
                }}
                error={(value) => checkErrors && (editingFeature.featureType?.dataType === 'RANGE') && checkIfIsNotEmpty(value, t_('Unit'))}
                forceCheckError={forceCheck}
                style={{ width: '100%' }}
                disabled={disabled}
              />
            </Box>

            <Box sx={{ gridColumn: { xs: '1 / -1', sm: '1 / 3' }, gridRow: { xs: 'auto', sm: '2 / 3' } }}>
              <SmartSelect
                label={t_('Inspection tool')}
                value={editingFeature.toolReferences?.map(t => t.code)[0]}
                selectableOptions={tools?.map((t) => t.code)}
                onChange={(newValue) => {
                  const selectedTools = tools?.filter(d => d.code === newValue)
                  const data = { ...editingFeature }
                  data.toolReferences = selectedTools
                  setEditingFeature(data)
                }}
                disableClearable={false}
                autoSelectFirst={false}
                style={{ width: '100%' }}
                disabled={disabled}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: '3 / 5' }, gridRow: { xs: 'auto', sm: '2 / 3' } }}>
              <SmartTextField
                label={t_('Reference')}
                value={editingFeature.reference}
                onChange={(newValue) => {
                  const data = { ...editingFeature }
                  data.reference = newValue
                  setEditingFeature(data)
                }}
                style={{ width: '100%' }}
                disabled={disabled}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: '5 / 7' }, gridRow: { xs: 'auto', sm: '2 / 3' } }}>
              <SmartTextField
                label={t_('Reference num.')}
                value={editingFeature.referenceNumber}
                onChange={(newValue) => {
                  const data = { ...editingFeature }
                  data.referenceNumber = newValue
                  setEditingFeature(data)
                }}
                checkError={(value) => checkIfIsNumber(value, t_('Reference num.'))}
                forceCheckError={forceCheck}
                style={{ width: '100%' }}
                disabled={disabled}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: '7 / 8' }, gridRow: { xs: 'auto', sm: '2 / 3' } }}>
              <SmartTextField
                label={t_('Serializable')}
                value={getSerializable(editingFeature.toolReferences)}
                disabled
                style={{ width: '100%' }}
              />
            </Box>

            <Box sx={{ gridColumn: { xs: '1 / -1', sm: '1 / 3' }, gridRow: { xs: 'auto', sm: '3 / 4' } }}>
              <SmartSelect
                label={t_('Inspection point')}
                value={editingFeature.inspectionPoint?.name}
                selectableOptions={inspectionPoints?.map((type) => type.name)}
                onChange={(newValue) => {
                  const selectedTools = inspectionPoints?.find(d => d.name === newValue)
                  const data = { ...editingFeature }
                  data.inspectionPoint = selectedTools
                  setEditingFeature(data)
                }}
                disableClearable={false}
                style={{ width: '100%' }}
                disabled={disabled}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: '3 / 5' }, gridRow: { xs: 'auto', sm: '3 / 4' } }}>
              <SmartTextField
                label={t_('Inspection point id')}
                value={editingFeature.inspectionPointId}
                onChange={(newValue) => {
                  const data = { ...editingFeature }
                  data.inspectionPointId = newValue
                  setEditingFeature(data)
                }}
                checkError={(value) => checkIfIsNumber(value, t_('Inspection point id'))}
                forceCheckError={forceCheck}
                style={{ width: '100%' }}
                disabled={disabled}
              />
            </Box>

            {/* Historia 44 — Question ampliada (D1/D4): fuera del flujo (`position:absolute`),
                anclada a las líneas del grid (columnas 8-12, filas 1-2 → Nominal.../Inspection
                tool...); la fila de Inspection point queda fuera. Al estar fuera de flujo no
                aporta tamaño (ni mínimo ni preferido) a esas filas — el alto/ancho de esta caja
                los calcula el navegador a partir del área real que delimitan esas líneas, ya
                fijada por el resto de campos, sin ningún ajuste manual. */}
            <Box sx={{ position: { xs: 'static', sm: 'absolute' }, inset: { sm: 0 }, gridColumn: { xs: '1 / -1', sm: '8 / 13' }, gridRow: { xs: 'auto', sm: '1 / 3' } }}>
              <SmartTextField
                label={t_('Question')}
                value={editingFeature.qualitativeAttribute}
                onChange={(newValue) => {
                  const data = { ...editingFeature }
                  data.qualitativeAttribute = newValue
                  setEditingFeature(data)
                }}
                disabled={!editingFeature.featureType || disabled}
                checkError={(value) => editingFeature.featureType?.name === qualitative && checkIfIsNotEmpty(value, t_('Question'))}
                forceCheckError={forceCheck}
                required={editingFeature.featureType?.dataType !== 'RANGE' && editingFeature.featureType?.dataType !== 'NO_RESPONSE'}
                style={{ width: '100%' }}
                multiline
                rows={QUESTION_VISIBLE_ROWS}
                sx={{
                  height: { sm: '100%' },
                  '& .MuiInputBase-root': { height: { sm: '100%' }, alignItems: 'flex-start' },
                  '& .MuiInputBase-input': { height: { sm: '100% !important' }, overflowY: 'auto' }
                }}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  )
}
export default FeatureForm
